import { gql } from "@apollo/client";
import {
  isInspections,
  isNumericCollectionType,
} from "./serviceItemHelpers";

export const SAVE_SERVICE = gql`
  mutation saveService($request: save_service_request!) {
    save_service(request: $request) {
      id
      isNew
      ticket_id
    }
  }
`;

function isInspectionItem(item) {
  return item?.isInspection || isInspections(item);
}

function inspectionPartRows(state) {
  if (state?.parts?.length) return state.parts;
  return [{ quantity: state?.quantity, partNo: state?.partNo }];
}

function firstInspectionPart(state) {
  return inspectionPartRows(state)[0] || { quantity: "", partNo: "" };
}

function collectionPartFields(item, state) {
  const raw = state?.unit != null ? String(state.unit).trim() : "";
  if (!raw) return {};
  if (isNumericCollectionType(item)) {
    const num = Number(raw);
    if (Number.isFinite(num)) return { quantity: num };
  }
  return { description: raw };
}

export function buildSaveServiceParts({ serviceItems, serviceItemStates, savedParts }) {
  const parts = [];
  for (const item of serviceItems || []) {
    const state = serviceItemStates?.[item.id];
    if (!state) continue;
    const saved = (savedParts || []).find((p) => Number(p.service_item_id) === Number(item.id));
    const row = { service_item_id: Number(item.id) };
    if (saved?.id) row.id = saved.id;
    if (isInspectionItem(item)) {
      const first = firstInspectionPart(state);
      const qtyStr = first.quantity != null ? String(first.quantity).trim() : "";
      const partNo = first.partNo != null ? String(first.partNo).trim() : "";
      if (qtyStr && !Number.isNaN(Number(qtyStr))) row.quantity = Number(qtyStr);
      if (partNo) row.part_no = partNo;
    } else {
      Object.assign(row, collectionPartFields(item, state));
    }
    parts.push(row);
  }
  return parts;
}

export function buildSaveServiceValues({
  serviceItems,
  serviceItemStates,
  serviceComment,
  savedValues,
}) {
  const values = [];
  const trimmed = serviceComment != null ? String(serviceComment).trim() : "";
  const serviceLevel = (savedValues || []).find((v) => !v.service_item_id);
  if (trimmed || serviceLevel) {
    const row = { status: 0, description: trimmed };
    if (serviceLevel?.id) row.id = serviceLevel.id;
    values.push(row);
  }
  for (const item of serviceItems || []) {
    if (!isInspectionItem(item)) continue;
    const state = serviceItemStates?.[item.id];
    if (!state) continue;
    const saved = (savedValues || []).find((v) => Number(v.service_item_id) === Number(item.id));
    const status = state.status === "action" ? 1 : state.status === "ok" ? 2 : 0;
    const row = {
      service_item_id: Number(item.id),
      status,
      description: state.comment != null ? String(state.comment).trim() : "",
    };
    if (saved?.id) row.id = saved.id;
    values.push(row);
  }
  return values;
}

export function buildPartsNeededTicket({
  serviceItems,
  serviceItemStates,
  crane,
  craneName,
  technicianName,
}) {
  const actionItems = (serviceItems || []).filter(
    (item) => isInspectionItem(item) && serviceItemStates?.[item.id]?.status === "action"
  );
  if (actionItems.length === 0 || !crane) return null;

  const lines = [];
  for (const item of actionItems) {
    const state = serviceItemStates[item.id] || {};
    for (const part of inspectionPartRows(state)) {
      const qtyStr = part.quantity != null ? String(part.quantity).trim() : "";
      const partNoStr = part.partNo != null ? String(part.partNo).trim() : "";
      if (!qtyStr && !partNoStr) continue;
      const qtyNum = qtyStr ? Number(qtyStr) : 1;
      const qtyDisplay = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
      const label = partNoStr || item.name || "part";
      lines.push(`${qtyDisplay} part(s) needed: ${label}`);
    }
  }

  if (lines.length === 0) return null;

  const techLabel = technicianName || "technician";
  const craneLabel = craneName ? `Crane: ${craneName}` : (crane ? `Crane #${crane}` : "");
  const header =
    `The following parts were marked as needed, but not\ninstalled during a service by ${techLabel}${craneLabel ? ` for ${craneLabel}` : ""}:`;

  return {
    name: craneName ? `Parts Needed - ${craneName}` : "Parts Needed - Service",
    description: `${header}\n${lines.join("\n")}`,
    ticket_type_id: 2,
    status_id: 1,
    crane_id: Number(crane),
  };
}

export function buildSaveServiceRequest({
  serviceId,
  serviceDefinitionId,
  craneId,
  userId,
  idempotencyKey,
  subscribe,
  serviceItems,
  serviceItemStates,
  serviceComment,
  savedParts,
  savedValues,
  partsNeededTicket,
}) {
  const request = {
    service_definition_id: Number(serviceDefinitionId),
    crane_id: Number(craneId),
    user_id: Number(userId),
    subscribe: Boolean(subscribe),
    parts: buildSaveServiceParts({ serviceItems, serviceItemStates, savedParts }),
    values: buildSaveServiceValues({
      serviceItems,
      serviceItemStates,
      serviceComment,
      savedValues,
    }),
  };
  if (serviceId) request.id = serviceId;
  if (!serviceId && idempotencyKey) request.idempotency_key = idempotencyKey;
  if (partsNeededTicket) request.parts_needed_ticket = partsNeededTicket;
  return request;
}

export function saveServiceRequestFromOffline(offline) {
  if (offline?.request) {
    const request = { ...offline.request };
    if (offline.service_id) request.id = offline.service_id;
    return request;
  }
  return buildSaveServiceRequest({
    serviceId: offline.service_id || undefined,
    serviceDefinitionId: offline.service_definition_id,
    craneId: offline.crane_id,
    userId: offline.user_id ?? offline.technician_id,
    idempotencyKey: offline.idempotency_key || `offline-${offline.key}`,
    subscribe: !offline.service_id,
    serviceItems: offline.serviceItems || [],
    serviceItemStates: offline.serviceItemStates || {},
    serviceComment: offline.serviceComment || "",
    savedParts: [],
    savedValues: [],
  });
}
